import { X } from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '#client/lib/cn';
import { Button } from './Button';

interface ModalProps {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  primaryAction?:
    { label: string; onAction: () => void; loading?: boolean; destructive?: boolean } | undefined;
  secondaryAction?: { label: string; onAction: () => void } | undefined;
  children: ReactNode;
  className?: string | undefined;
}

/**
 * Uses the native `<dialog>` element so focus trapping, Escape handling and
 * the top layer come from the platform rather than from a JS implementation.
 */
export function Modal({
  open,
  title,
  onClose,
  primaryAction,
  secondaryAction,
  children,
  className,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  // `cancel` fires on Escape; route it through onClose so state stays in sync.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  if (!open) return null;

  return createPortal(
    // Named by its own heading, so it is announced as more than just "dialog"
    // with no indication of what it is asking.
    <dialog ref={dialogRef} className={cn('c-modal', className)} aria-labelledby={titleId}>
      <div className="c-modal__header">
        <h2 className="c-modal__title" id={titleId}>
          {title}
        </h2>
        <button type="button" className="c-modal__close" onClick={onClose} aria-label="Close">
          <X aria-hidden="true" />
        </button>
      </div>

      <div className="c-modal__body">{children}</div>

      {(primaryAction || secondaryAction) && (
        <div className="c-modal__footer">
          {secondaryAction && (
            <Button onClick={secondaryAction.onAction}>{secondaryAction.label}</Button>
          )}
          {primaryAction && (
            <Button
              variant={primaryAction.destructive ? 'danger' : 'primary'}
              loading={primaryAction.loading}
              onClick={primaryAction.onAction}
            >
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </dialog>,
    document.body,
  );
}
